'use strict';
import isString from 'lodash/isString';
import {getCollection, getId} from '../../core-utilities/domain/ReduxUtils';
import {getScheduleStatus} from './utils';
const angular = require('angular');
const {isDate, toJson} = angular;


export function AbstractTaskProvider() {
  return {
    $get: [
      '$injector',
      '$timeout',
      'AbstractDomainClass',
      'AuthService',
      'OrgIdMixin',
      'MetricMixin',
      'FeedableMixin',
      'fetchUtils',
      'restUtils',
      'FirebaseConnectionService',
      'FirebaseQueueService',
      'ROOT_API_COLLECTION',
      'ROOT_API_SPEC',
      function (
        $injector,
        $timeout,
        AbstractDomainClass,
        AuthService,
        OrgIdMixin,
        MetricMixin,
        FeedableMixin,
        fetchUtils,
        restUtils,
        FirebaseConnectionService,
        FirebaseQueueService,
        ROOT_API_COLLECTION,
        ROOT_API_SPEC,
      ) {

        function $$AbstractTask() {
          //AbstractDomainClass.call(this, Class, data); // args: Class, data
        }

        $$AbstractTask.__inheritFrom = function (Class, className, collection) {
          AbstractDomainClass.__inheritFrom(Class, className, collection);
          OrgIdMixin(Class);
          MetricMixin(Class);
          FeedableMixin(Class);

          var projectProperty = {type: 'Domain', domainClass: 'ProjectDomain', belongsTo: true};
          // Kind of a configuration hack to keep things simple for now
          if (className == 'TaskDomain') projectProperty.links = 'tasks';

          Class.$constraints.addConstraints({
            name: {type: 'string'},
            description: {type: 'string', widget: 'textarea', nullable: true},
            isPrivate: {type: 'boolean', defaultValue: false},

            /* Date stuff */
            submittedOn: {type: 'Date', immutable: true, nullable: true},
            submittedBy: {type: 'string', immutable: true, nullable: true},
            approvedOn: {type: 'Date', immutable: true, nullable: true},
            approvedBy: {type: 'string', immutable: true, nullable: true},
            cancelledOn: {type: 'Date', immutable: true, nullable: true},
            cancelledBy: {type: 'string', immutable: true, nullable: true},
            scheduled: {type: 'Date', defaultValue: null, nullable: true},
            scheduledBy: {type: 'DomainLink', domainClass: 'PersonDomain', nullable: true},
            /* whether the scheduled completion is currently time specific */
            /* If false, all dates are stored as midnight server time */
            useTime: {type: 'boolean', defaultValue: false},
            /* expected or 'standard' duration.
             actual duration is calculated from completedOn - startedOn
             or possibly accumulated with a timers system not yet designed */
            duration: {type: 'number', nullable: true},

            status: {
              type: 'string',
              inList: ['pending', 'cancelled', 'in_progress', 'submitted', 'has_rejection', 'rejected', 'approved'],
              defaultValue: 'pending'
            },
            assignedTo: {type: 'Domain', nullable: true},

            milestone: {type: 'Domain', domainClass: 'MilestoneDomain', nullable: true},
            project: projectProperty,
            org: {type: 'string'}, // Organization ID
            createdBy: {type: 'DomainLink', domainClass: 'UserDomain', nullable: true},
            dateCreated: {type: 'Date'},
            lastUpdated: {type: 'Date'},

            conversation: {type: 'LinkedDocument', domainClass: 'ConversationDomain', link: 'target'},

            priorities: {type: 'DomainCollection', domainClass: 'UserPinDomain', link: 'target'},

            /* Task order dependency system */
            dependsOn: {type: 'DomainCollection', domainClass: Class.$name, relationClass: 'TaskDependencyDomain'},
            dependedOnBy: {type: 'DomainCollection', domainClass: Class.$name},

            approvalBy: {type: 'DomainCollection', domainClass: 'PersonDomain', relationClass: 'TaskApprovalDomain'},
            approvalByTeams: {type: 'DomainCollection', domainClass: 'TeamDomain'}
          });

          Class.getPin = getPin;
          Class.getPinId = getPinId;
          Class.getTaskDate = getTaskDate;
          Class.getTaskUseTime = getTaskUseTime;
          Class.getScheduleStatus = getScheduleStatus;

          //Class.prototype << $$AbstractTask.prototype

          Class.updateStatus = restUtils.updateStatus(Class);
          Class.prototype.updateStatus = restUtils.updateStatusProto(Class);
          Class.prototype.updateSchedule = $$AbstractTask.prototype.updateSchedule;
          Class.prototype.overrideSchedule = $$AbstractTask.prototype.overrideSchedule;
          Class.prototype.getScheduleStatus = $$AbstractTask.prototype.getScheduleStatus;
          Class.prototype.canSweep = $$AbstractTask.prototype.canSweep;
          Class.prototype.isComplete = $$AbstractTask.prototype.isComplete;
          Class.prototype.isCompleteSync = $$AbstractTask.prototype.isCompleteSync;
          Class.prototype.getPin = $$AbstractTask.prototype.getPin;
          Class.prototype.isPinned = $$AbstractTask.prototype.isPinned;
          Class.prototype.toString = $$AbstractTask.prototype.toString;

          Class.getStatusPermission = collection.indexOf('asset') > -1 ? getAssetStatusPermission : getStatusPermission;

          // This is a short term hack to tasks broken by incorrect updates or imports
          Class.$doBeforeValidation(__fixUseTime);
        };

        //$$AbstractTask.prototype methods go here

        $$AbstractTask.prototype.updateSchedule = function updateSchedule(schedule) {
          var dateChanged = schedule.scheduled !== this.scheduled;
          var timeChanged = schedule.useTime !== this.useTime;
          var assignedToChanged = schedule.assignedTo !== this.assignedTo;
          // the task will currently be edited due to the way <task-duration> works
          var durationChanged = schedule.duration !== this.$getOriginalValue('duration');

          if (!dateChanged && !assignedToChanged && !timeChanged) {
            if (durationChanged)
              return this.update();
            else
              return Promise.reject({code: 'tasks.schedule.change.error.same.message', rejectedValue: schedule});
          }

          if (isDate(schedule.scheduled) && !schedule.useTime) schedule.scheduled.setHours(12, 0, 0, 0);

          var update = isString(schedule.comment) ? {comment: schedule.comment} : {};

          if (dateChanged) update.scheduled = schedule.scheduled ? schedule.scheduled.getTime() : null;
          if (timeChanged) update.useTime = schedule.useTime;
          if (assignedToChanged) {
            if (getId(schedule.assignedTo)) {
              update.assignedTo = {
                id: getId(schedule.assignedTo),
                collection: getCollection(schedule.assignedTo),
              };
            } else {
              update.assignedTo = null;
            }
          }

          const request = {
            method: 'PATCH',
            mode: 'cors',
            cache: 'default',
            contentType: 'json',
            responseType: 'json',
            body: update,
          };

          const url = `${fetchUtils.API_URL}/api/${getCollection(this)}/schedule/${getId(this)}`;
          return fetchUtils.requestWithAuth(url, request)
            .catch(firebaseFallback.bind(this)) /* Temporary fallback */
            .then(((response) => {
              this.$revert();
              return response;
            }));

          function firebaseFallback() {
            if (!FirebaseConnectionService.online)
              return Promise.reject({code: 'default.error.offline.message'});
            const method = getCollection(this).indexOf('asset') > -1
              ? 'asset-tasks/schedule/update'
              : 'tasks/schedule/update';
            const fallbackRequest = {
              method: method,
              data: toJson({taskId: getId(this), ...update}),
            };
            // eslint-disable-next-line max-len
            return FirebaseQueueService.addTask(fallbackRequest, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC);
          }
        };

        $$AbstractTask.prototype.overrideSchedule = function overrideSchedule(schedule) {
          var dateChanged = schedule.scheduled !== this.scheduled;
          var timeChanged = schedule.useTime !== this.useTime;
          var submittedOnChanged = schedule.submittedOn !== this.submittedOn;

          if (!dateChanged && !submittedOnChanged && !timeChanged) {
            return Promise.reject({code: 'tasks.schedule.same.message', rejectedValue: schedule});
          } else if (!isDate(schedule.submittedOn)) {
            return Promise.reject({code: 'tasks.schedule.submittedOn.required.message', rejectedValue: schedule});
          }

          if (isDate(schedule.scheduled) && !schedule.useTime) schedule.scheduled.setHours(12, 0, 0, 0);

          var update = isString(schedule.comment) ? {comment: schedule.comment} : {};

          if (dateChanged) update.scheduled = schedule.scheduled ? schedule.scheduled.getTime() : null;
          if (timeChanged) update.useTime = schedule.useTime;
          if (submittedOnChanged) update.submittedOn = schedule.submittedOn.getTime();

          const request = {
            method: 'PATCH',
            mode: 'cors',
            cache: 'default',
            contentType: 'json',
            responseType: 'json',
            body: update,
          };

          const url = `${fetchUtils.API_URL}/api/${getCollection(this)}/schedule-override/${getId(this)}`;
          return fetchUtils.requestWithAuth(url, request)
            .catch(firebaseFallback.bind(this)) /* Temporary fallback */
            .then(((response) => {
              this.$revert();
              return response;
            }));

          function firebaseFallback() {
            if (!FirebaseConnectionService.online)
              return Promise.reject({code: 'default.error.offline.message'});
            const method = getCollection(this).indexOf('asset') > -1
              ? 'asset-tasks/schedule/override'
              : 'tasks/schedule/override';
            const fallbackRequest = {
              method: method,
              data: toJson({taskId: getId(this), ...update}),
            };
            // eslint-disable-next-line max-len
            return FirebaseQueueService.addTask(fallbackRequest, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC);
          }
        };

        $$AbstractTask.prototype.canSweep = function canSweep() {
          return this.status === 'submitted' || this.status === 'approved' || this.status === 'cancelled';
        };

        // the home screen currently requires an async isComplete method
        $$AbstractTask.prototype.isComplete = function isTaskComplete() {
          return Promise.resolve(this.isCompleteSync());
        };

        $$AbstractTask.prototype.isCompleteSync = function isTaskCompleteSync() {
          return this.status === 'submitted' || this.status === 'approved';
        };

        $$AbstractTask.prototype.getPin = function getPinProto() {
          return getPin(this);
        };

        function getPinId(task) {
          const userId = AuthService.getUserId();
          const UserPinDomain = $injector.get('UserPinDomain');
          return UserPinDomain.constructId(userId, getId(task));
        }

        function getPin(task) {
          return AuthService.getCurrentUser().then(function (user) {
            const UserPinDomain = $injector.get('UserPinDomain');
            const pinId = UserPinDomain.constructId(getId(user), getId(task));

            return UserPinDomain.$get(pinId);
          });
        }

        $$AbstractTask.prototype.isPinned = function isPinned() {
          return this.getPin().then(function (pin) {
            return !!pin && pin.isPinned();
          });
        };

        $$AbstractTask.prototype.getScheduleStatus = function () {
          return getScheduleStatus(this);
        };

        $$AbstractTask.prototype.toString = function () {
          return this.name;
        };

        function __fixUseTime(instance) {
          if (instance.useTime === null) instance.useTime = false;
        }

        return $$AbstractTask;
      }]
  };
}

export function getTaskDate(task) {
  if (task) {
    switch (task.status) {
      case 'submitted':
      case 'approved':
        return task.submittedOn;
      case 'cancelled':
        return task.cancelledOn;
      default:
        return task.scheduled;
    }
  }
}

export function getTaskUseTime(task) {
  if (task) {
    switch (task.status) {
      case 'submitted':
      case 'approved':
      case 'cancelled':
        return true;
      default:
        return task.useTime;
    }
  }
}

export function getStatusPermission(status) {
  switch (status) {
    case 'pending':
    case 'in_progress':
    case 'submitted':
      return 'task_status_submit';
    case 'cancelled':
    case 'rejected':
    case 'approved':
      return 'task_status_approve';
    default:
      return null
  }
}

export function getAssetStatusPermission(status) {
  return 'asset_' + getStatusPermission(status);
}



// WEBPACK FOOTER //
// ./src/js/tasks/domain/$$AbstractTask.js